/**
 * CommonDialog.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * A reusable MUI Dialog shell.
 *
 * Props:
 *   open        {boolean}        — controls visibility
 *   onClose     {function}       — fires when backdrop clicked or ESC pressed
 *   title       {string|ReactNode} — dialog header title
 *   children    {ReactNode}      — body content
 *   maxWidth    {string}         — 'xs'|'sm'|'md'|'lg'|'xl' (default 'sm')
 *   fullWidth   {boolean}        — stretch to maxWidth (default true)
 *   persistent  {boolean}        — true = prevent close on backdrop/ESC click
 *                                   (dialog shakes to signal it's locked)
 *   titleIcon   {ReactNode}      — any lucide-react icon to show left of title
 *                                   e.g. <UserPlus size={18} />
 */

import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  Slide,
  Box
} from '@mui/material';
import { X } from 'lucide-react';
import { CSS_PRIMARY } from '../../../constants/style';

/* ── Slide-up transition ─────────────────────────────────────────────────── */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ── Shake keyframe (injected once) ─────────────────────────────────────── */
if (typeof document !== 'undefined' && !document.getElementById('__cd_shake__')) {
  const style = document.createElement('style');
  style.id = '__cd_shake__';
  style.innerHTML = `
    @keyframes __cd_shake {
      0%,100% { transform: translateX(0); }
      20%      { transform: translateX(-6px); }
      40%      { transform: translateX(6px);  }
      60%      { transform: translateX(-4px); }
      80%      { transform: translateX(4px);  }
    }
    .cd-shake-anim { animation: __cd_shake 0.38s ease; }
  `;
  document.head.appendChild(style);
}

/* ════════════════════════════════════════════════════════════════════════════
   CommonDialog
   ════════════════════════════════════════════════════════════════════════════ */
const CommonDialog = ({
  open = false,
  onClose,
  title = 'Dialog',
  children,
  maxWidth,
  fullWidth,
  persistent = false, // ← NEW: locks dialog; shakes on outside click / ESC
  titleIcon = null, // ← NEW: lucide-react icon element shown beside title
  fullScreen = false
}) => {
  const paperRef = useRef(null);
  const [shaking, setShaking] = useState(false);

  /* Fires when backdrop is clicked or ESC is pressed */
  const handleClose = (_event, reason) => {
    if (persistent) {
      // Shake the dialog to signal it cannot be dismissed
      if (!shaking) {
        setShaking(true);
        setTimeout(() => setShaking(false), 400);
      }
      return;
    }
    onClose?.(_event, reason);
  };

  /* Close button only works when not persistent */
  const handleCloseButton = () => {
    onClose?.();
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      maxWidth={fullScreen ? false : maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        ref: paperRef,
        className: shaking ? 'cd-shake-anim' : undefined
      }}
      slots={{
        transition: SlideUp
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.75,
          bgcolor: '#fafafa',
          gap: 1.5
        }}
      >
        {/* Left side: optional icon + title */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flex: 1,
            minWidth: 0
          }}
        >
          {titleIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 2,
                bgcolor: 'rgba(46,204,113,0.10)',
                color: `var(--primary, ${CSS_PRIMARY})`,
                flexShrink: 0
              }}
            >
              {titleIcon}
            </Box>
          )}

          <Typography
            variant="subtitle1"
            fontWeight={700}
            fontSize="0.97rem"
            color="text.primary"
            letterSpacing="-0.01em"
            noWrap
          >
            {title}
          </Typography>
        </Box>

        {/* Right side: close button (dimmed when persistent) */}
        <IconButton
          size="small"
          onClick={handleCloseButton}
          aria-label="Close dialog"
          sx={{
            color: 'text.secondary',
            bgcolor: 'grey.100',
            borderRadius: 2,
            flexShrink: 0,
            cursor: 'pointer',
            '&:hover': { bgcolor: '#fee2e2', color: 'error.main' },
            transition: 'all 0.15s ease'
          }}
        >
          <X size={16} />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <DialogContent sx={{ px: 1.5, py: 1.5 }}>{children}</DialogContent>
    </Dialog>
  );
};

export default CommonDialog;
